import { CacheProvider } from "@emotion/react";
import { createTheme, CssBaseline, Theme, ThemeProvider } from "@mui/material";
import { IApiEpUserAuthorized } from "@smp/api";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { getThemeOptions } from "@/config";
import { createEmotionCache } from "@/utils";

import { IContextUi } from "./ui.types";

const ContextUi = createContext<IContextUi>(null as unknown as IContextUi);

const cache = createEmotionCache();

export const ProviderUi = ({ children }: PropsWithChildren<{}>) => {
  const [theme, setTheme] = useState<Theme>(
    createTheme(getThemeOptions(import.meta.env.SSR)),
  );

  const themeUpdateOnMeChanged = useCallback((me: IApiEpUserAuthorized) => {
    setTheme((theme) =>
      createTheme({
        ...theme,
        ...{
          props: {
            MuiButtonBase: {
              disableRipple: !me.settings.ui.is_animation_enabled,
            },
          },
          transitions: me.settings.ui.is_animation_enabled
            ? {}
            : { create: () => "none" },
        },
      }),
    );
  }, []);

  return useMemo(
    () => (
      <ContextUi.Provider
        value={{
          actions: { themeUpdateOnMeChanged },
          state: { theme },
        }}
      >
        {!import.meta.env.SSR ? (
          <CacheProvider value={cache}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              {children}
            </ThemeProvider>
          </CacheProvider>
        ) : (
          <ThemeProvider theme={theme}>
            <CssBaseline />
            {children}
          </ThemeProvider>
        )}
      </ContextUi.Provider>
    ),
    [children, theme, themeUpdateOnMeChanged],
  );
};

export const useContextUi = () => useContext(ContextUi);
